import { create } from 'zustand';
import { CONFIG } from '../config/ports';
import { normalizeToBase } from '../utils/symbolUtils';

// Alert types matching backend
export type AlertType = 'price_above' | 'price_below' | 'volume_spike' | 'volume_above';
export type TriggerMode = 'once_per_minute' | 'once_per_bar' | 'on_cross';
export type CrossDirection = 'from_above' | 'from_below' | 'either';

export interface Alert {
  id: string;
  symbol: string;
  alertType: AlertType;
  price: number | null;
  volumeMultiplier: number | null;
  volumeThreshold: number | null;  // Absolute volume threshold (for volume_above)
  volumeLookback: number | null;
  triggerMode: TriggerMode;
  crossDirection: CrossDirection | null;
  activeSessions: string; // "all", "rth", "premarket", "postmarket", "overnight" (PST, comma-separated)
  paused: boolean;
  createdAt: number;
  updatedAt: number;
  lastTriggeredAt: number | null;
}

export interface CreateAlertRequest {
  symbol: string;
  alertType: AlertType;
  price?: number;
  volumeMultiplier?: number;
  volumeThreshold?: number;  // Absolute volume threshold (for volume_above)
  volumeLookback?: number;
  triggerMode?: TriggerMode;
  crossDirection?: CrossDirection;
}

export interface UpdateAlertRequest {
  alertType?: AlertType;
  price?: number;
  volumeMultiplier?: number;
  volumeThreshold?: number;
  volumeLookback?: number;
  triggerMode?: TriggerMode;
  crossDirection?: CrossDirection;
  paused?: boolean;
}

interface AlertState {
  // Alerts data
  alerts: Alert[];
  isLoading: boolean;
  error: string | null;

  // Placement mode (unified - pane clicked determines alert type)
  placementMode: boolean;
  pendingAlertPrice: number | null;

  // Settings modal
  editingAlert: Alert | null;
  showSettingsModal: boolean;

  // Alerts panel
  showAlertsPanel: boolean;

  // Actions
  fetchAlerts: (symbol: string) => Promise<void>;
  fetchAllAlerts: () => Promise<void>;
  createAlert: (request: CreateAlertRequest) => Promise<Alert | null>;
  updateAlert: (id: string, updates: UpdateAlertRequest) => Promise<boolean>;
  deleteAlert: (id: string) => Promise<boolean>;
  pauseAlert: (id: string) => Promise<boolean>;
  resumeAlert: (id: string) => Promise<boolean>;

  // Placement mode
  enterPlacementMode: () => void;
  exitPlacementMode: () => void;
  togglePlacementMode: () => void;
  setPendingAlertPrice: (price: number | null) => void;

  // Settings modal
  openSettingsModal: (alert: Alert) => void;
  closeSettingsModal: () => void;

  // Alerts panel
  openAlertsPanel: () => void;
  closeAlertsPanel: () => void;
  toggleAlertsPanel: () => void;

  // Real-time updates from WebSocket
  addAlert: (alert: Alert) => void;
  updateAlertFromSocket: (id: string, data: Record<string, any>) => void;
  removeAlert: (id: string) => void;
}

const API_BASE = CONFIG.getBackendUrl();

export const useAlertStore = create<AlertState>((set, get) => ({
  // Initial state
  alerts: [],
  isLoading: false,
  error: null,
  placementMode: false,
  pendingAlertPrice: null,
  editingAlert: null,
  showSettingsModal: false,
  showAlertsPanel: false,

  // Fetch alerts for a specific symbol
  fetchAlerts: async (symbol: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_BASE}/api/alerts?symbol=${encodeURIComponent(normalizeToBase(symbol))}`);
      if (!response.ok) throw new Error('Failed to fetch alerts');
      const data = await response.json();
      set({ alerts: data.alerts || [], isLoading: false });
      console.log(`[DEBUG:ALERT] fetchAlerts(${symbol}): received ${(data.alerts || []).length} alerts, ids=[${(data.alerts || []).map((a: any) => a.id).join(',')}]`);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  // Fetch all alerts (for alerts panel)
  fetchAllAlerts: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_BASE}/api/alerts`);
      if (!response.ok) throw new Error('Failed to fetch alerts');
      const data = await response.json();
      set({ alerts: data.alerts || [], isLoading: false });
      console.log(`[DEBUG:ALERT] fetchAllAlerts: received ${(data.alerts || []).length} alerts`);
    } catch (error) {
      console.error('Error fetching all alerts:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  // Create a new alert
  createAlert: async (request: CreateAlertRequest) => {
    set({ isLoading: true, error: null });
    try {
      const normalizedRequest = {
        ...request,
        symbol: normalizeToBase(request.symbol),
      };
      const response = await fetch(`${API_BASE}/api/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalizedRequest),
      });
      if (!response.ok) throw new Error('Failed to create alert');
      const alert = await response.json();
      if (alert.error) throw new Error(alert.error);

      // Add to local state.
      //
      // The engine broadcasts alert:created to every socket including this one,
      // so addAlert may already have inserted this alert before the POST
      // resolved. Appending unconditionally then put two entries with the SAME
      // id in the store, which collided key={alert.id} on the chart; duplicate
      // keys break React's element-to-DOM matching and left one AlertLine's
      // imperatively-set `top` frozen at a stale price scale — two lines, same
      // price, different heights. Prefer the POST response over whatever the
      // socket inserted: it is the authoritative server object.
      set(state => ({
        alerts: state.alerts.some(a => a.id === alert.id)
          ? state.alerts.map(a => (a.id === alert.id ? alert : a))
          : [...state.alerts, alert],
        isLoading: false,
        placementMode: false,
        pendingAlertPrice: null,
      }));
      console.log(`[DEBUG:ALERT] createAlert: added id=${alert.id}, symbol=${alert.symbol}, price=${alert.price}, total alerts now=${get().alerts.length}`);
      return alert;
    } catch (error) {
      console.error('Error creating alert:', error);
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },

  // Update an alert
  updateAlert: async (id: string, updates: UpdateAlertRequest) => {
    // Optimistic update: apply immediately so the line moves without delay
    const prevAlerts = get().alerts;
    set(state => ({
      alerts: state.alerts.map(a =>
        a.id === id ? { ...a, ...updates, updatedAt: Date.now() } : a
      ),
    }));

    try {
      const response = await fetch(`${API_BASE}/api/alerts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update alert');
      const result = await response.json();
      if (result.error) throw new Error(result.error);
      return true;
    } catch (error) {
      console.error('Error updating alert:', error);
      // Revert on failure
      set({ alerts: prevAlerts, error: (error as Error).message });
      return false;
    }
  },

  // Delete an alert
  deleteAlert: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/alerts/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete alert');
      const result = await response.json();
      if (result.error) throw new Error(result.error);

      // Remove from local state
      set(state => ({
        alerts: state.alerts.filter(a => a.id !== id),
      }));
      return true;
    } catch (error) {
      console.error('Error deleting alert:', error);
      set({ error: (error as Error).message });
      return false;
    }
  },

  // Pause an alert
  pauseAlert: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/alerts/${id}/pause`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to pause alert');
      const result = await response.json();
      if (result.error) throw new Error(result.error);

      // Update local state
      set(state => ({
        alerts: state.alerts.map(a =>
          a.id === id ? { ...a, paused: true } : a
        ),
      }));
      return true;
    } catch (error) {
      console.error('Error pausing alert:', error);
      set({ error: (error as Error).message });
      return false;
    }
  },

  // Resume an alert
  resumeAlert: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/alerts/${id}/resume`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to resume alert');
      const result = await response.json();
      if (result.error) throw new Error(result.error);

      // Update local state
      set(state => ({
        alerts: state.alerts.map(a =>
          a.id === id ? { ...a, paused: false } : a
        ),
      }));
      return true;
    } catch (error) {
      console.error('Error resuming alert:', error);
      set({ error: (error as Error).message });
      return false;
    }
  },

  // Placement mode actions
  enterPlacementMode: () => set({ placementMode: true, pendingAlertPrice: null }),
  exitPlacementMode: () => set({ placementMode: false, pendingAlertPrice: null }),
  togglePlacementMode: () => set(state => ({ placementMode: !state.placementMode, pendingAlertPrice: null })),
  setPendingAlertPrice: (price: number | null) => set({ pendingAlertPrice: price }),

  // Settings modal actions
  openSettingsModal: (alert: Alert) => set({ editingAlert: alert, showSettingsModal: true }),
  closeSettingsModal: () => set({ editingAlert: null, showSettingsModal: false }),

  // Alerts panel actions
  openAlertsPanel: () => set({ showAlertsPanel: true }),
  closeAlertsPanel: () => set({ showAlertsPanel: false }),
  toggleAlertsPanel: () => set(state => ({ showAlertsPanel: !state.showAlertsPanel })),

  // Real-time updates from WebSocket
  addAlert: (alert: Alert) => {
    const isDuplicate = get().alerts.some(a => a.id === alert.id);
    console.log(`[DEBUG:ALERT] addAlert (from socket): id=${alert.id}, symbol=${alert.symbol}, isDuplicate=${isDuplicate}, current count=${get().alerts.length}`);
    set(state => {
      // Check if already exists
      if (state.alerts.some(a => a.id === alert.id)) return state;
      return { alerts: [...state.alerts, alert] };
    });
  },

  updateAlertFromSocket: (id: string, data: Record<string, any>) => {
    // Backend emits two formats:
    //   { id, updates: { price, alertType, ... } }  — from PUT /api/alerts/:id
    //   { id, paused: true/false }                   — from pause/resume
    // Unwrap nested 'updates' if present, otherwise spread flat fields
    const updates = data.updates ?? data;
    // Remove 'id' and 'updates' keys so we don't pollute the alert object
    const { id: _id, updates: _nested, ...fields } = updates;
    console.log(`[DEBUG:ALERT] updateAlertFromSocket: id=${id}, fields=${Object.keys(fields).join(',')}`);
    set(state => ({
      alerts: state.alerts.map(a =>
        a.id === id ? { ...a, ...fields } : a
      ),
    }));
  },

  removeAlert: (id: string) => {
    console.log(`[DEBUG:ALERT] removeAlert: id=${id}, was present=${get().alerts.some(a => a.id === id)}`);
    set(state => ({
      alerts: state.alerts.filter(a => a.id !== id),
    }));
  },
}));
